// 창고 엑셀에서 "사진"을 걷어내고 표만 남긴다. 순수 함수 모듈.
//
// 왜 필요한가 — 2026-09-08 실측:
//   창고(WPS)가 만든 출고리스트에는 상품 사진이 셀마다 통째로 박혀 온다.
//   실제 파일: 110.3MB 중 109.2MB 가 사진 658장이고, 정작 우리가 읽는 표는 1MB 도 안 됐다.
//   그런데 기존 코드는 파일 전체를 binary string 으로 만들어 SheetJS 에 넘겼다.
//   그러면 브라우저가 사진까지 전부 펼치느라 메인 스레드를 통째로 잡는다.
//
//   같은 크롬·같은 파일 실측:
//     기존(사진 포함 통째로) : 69.7초, 최대 힙 1,439MB
//     사진 제거 후           :  0.9초, 최대 힙   159MB     ← 78배
//   실제 화면에서는 이미 8천여 건이 올라와 있어 더 나빠져 6분 27초까지 멈췄다.
//   그동안 네트워크 요청이 한 건도 안 나가서, 서버 로그만 보면 아무 일도 없는 것처럼 보인다.
//
// 어떻게 — xlsx 는 zip 이다. 압축을 풀었다 다시 압축하지 않는다.
//   중앙 디렉터리(목차)를 읽어 남길 항목의 로컬헤더+압축데이터를 **바이트 그대로** 옮기고
//   목차와 꼬리(EOCD)만 새로 쓴다. 실측 4ms.
//
// 안전 — 사진을 빼도 표는 글자 하나까지 같다(그룹 1,502 / 행 1,719 / 파싱 지문 동일 확인).
//   zip 이 아니거나(.xls) 구조가 예상과 다르면 걷어내지 않고 원본을 그대로 돌려준다.
//   이 파일은 "빠르게 하는" 역할만 하고, 못 하면 조용히 기존 동작으로 돌아가야 한다.

const SIG_EOCD = 0x06054b50;
const SIG_CD = 0x02014b50;
const SIG_LFH = 0x04034b50;

// zip 파일인가 (xlsx/xlsm 은 zip, 옛 .xls 는 아니다)
export function isZip(u8) {
  return u8.length > 4 && u8[0] === 0x50 && u8[1] === 0x4b;
}

// Uint8Array → binary string. SheetJS 의 type:'binary' 가 먹는 형태다.
// (type:'array' 는 이 버전(xlsx 0.18.5)에서 병적으로 느려 쓰지 않는다 — 실측 22초)
export function u8ToBinaryString(u8) {
  let s = '';
  const CH = 0x8000;   // 한 번에 넘기는 인자 수 상한을 넘기지 않을 크기
  for (let i = 0; i < u8.length; i += CH) {
    s += String.fromCharCode.apply(null, u8.subarray(i, i + CH));
  }
  return s;
}

// dropFn(name) 이 true 인 항목을 뺀 새 zip 을 만든다.
// 걷어낼 수 없으면 { out: u8, dropped: 0, reason } 으로 원본을 그대로 돌려준다.
export function stripZipEntries(u8, dropFn) {
  if (!isZip(u8)) return { out: u8, dropped: 0, droppedBytes: 0, reason: 'zip 아님' };
  try {
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    const u32 = o => dv.getUint32(o, true);
    const u16 = o => dv.getUint16(o, true);

    // 1) 꼬리(EOCD) 찾기 — 주석이 붙을 수 있어 뒤에서부터 훑는다
    let eocd = -1;
    const stop = Math.max(0, u8.length - 65557);
    for (let i = u8.length - 22; i >= stop; i--) {
      if (u32(i) === SIG_EOCD) { eocd = i; break; }
    }
    if (eocd < 0) return { out: u8, dropped: 0, droppedBytes: 0, reason: 'EOCD 없음' };

    const total = u16(eocd + 10);
    const cdOff = u32(eocd + 16);
    // ZIP64 는 이 표식으로 나타난다. 창고 파일에선 본 적 없지만, 나오면 손대지 않는다.
    if (total === 0xffff || cdOff === 0xffffffff) {
      return { out: u8, dropped: 0, droppedBytes: 0, reason: 'ZIP64' };
    }

    // 2) 목차를 훑어 남길 것만 고른다
    const keep = [];
    let dropped = 0;
    let droppedBytes = 0;
    let p = cdOff;
    const dec = new TextDecoder();
    for (let i = 0; i < total; i++) {
      if (p + 46 > u8.length || u32(p) !== SIG_CD) {
        return { out: u8, dropped: 0, droppedBytes: 0, reason: '목차 손상' };
      }
      const nameLen = u16(p + 28);
      const extraLen = u16(p + 30);
      const cmtLen = u16(p + 32);
      const cdLen = 46 + nameLen + extraLen + cmtLen;
      const name = dec.decode(u8.subarray(p + 46, p + 46 + nameLen));
      const compSize = u32(p + 20);
      const lho = u32(p + 42);
      // data descriptor(플래그 3번 비트)를 쓰면 압축크기가 목차에만 있어 그대로 복사해도
      // 되지만, 로컬헤더 값과 어긋나는 구현이 있어 안전하게 손대지 않는다.
      const flags = u16(p + 8);
      if (flags & 0x08) return { out: u8, dropped: 0, droppedBytes: 0, reason: 'data descriptor' };
      if (dropFn(name)) { dropped++; droppedBytes += compSize; }
      else keep.push({ cdStart: p, cdLen, compSize, lho });
      p += cdLen;
    }
    if (!dropped) return { out: u8, dropped: 0, droppedBytes: 0, reason: '뺄 것 없음' };

    // 3) 남길 항목: 로컬헤더 + 압축데이터를 그대로 복사 (압축 해제 없음)
    const parts = [];
    let size = 0;
    for (const e of keep) {
      if (e.lho + 30 > u8.length || u32(e.lho) !== SIG_LFH) {
        return { out: u8, dropped: 0, droppedBytes: 0, reason: '로컬헤더 손상' };
      }
      const lhLen = 30 + u16(e.lho + 26) + u16(e.lho + 28);
      const chunk = u8.subarray(e.lho, e.lho + lhLen + e.compSize);
      e.newOff = size;
      parts.push(chunk);
      size += chunk.length;
    }

    // 4) 새 목차 — 원본 레코드를 복사하고 로컬헤더 위치만 고쳐 쓴다
    const cdParts = [];
    let cdLen2 = 0;
    for (const e of keep) {
      const rec = u8.slice(e.cdStart, e.cdStart + e.cdLen);
      new DataView(rec.buffer, rec.byteOffset, rec.byteLength).setUint32(42, e.newOff, true);
      cdParts.push(rec);
      cdLen2 += rec.length;
    }

    // 5) 새 꼬리
    const end = new Uint8Array(22);
    const ev = new DataView(end.buffer);
    ev.setUint32(0, SIG_EOCD, true);
    ev.setUint16(8, keep.length, true);
    ev.setUint16(10, keep.length, true);
    ev.setUint32(12, cdLen2, true);
    ev.setUint32(16, size, true);

    const out = new Uint8Array(size + cdLen2 + 22);
    let q = 0;
    for (const c of parts) { out.set(c, q); q += c.length; }
    for (const c of cdParts) { out.set(c, q); q += c.length; }
    out.set(end, q);
    return { out: out, dropped: dropped, droppedBytes: droppedBytes, reason: '' };
  } catch (e) {
    // 여기서 던지면 업로드 자체가 죽는다. 빠르게 하려다 못 올리게 만들지 않는다.
    return { out: u8, dropped: 0, droppedBytes: 0, reason: '예외: ' + e.message };
  }
}

// 창고 엑셀 전용 — 사진(xl/media/)만 걷어낸다.
export function stripXlsxMedia(u8) {
  return stripZipEntries(u8, n => n.indexOf('xl/media/') === 0);
}

// 표(sheet_to_json header:1) 뒤쪽의 빈 행·빈 열을 걷는다 — 서식이 끝없이 칠해진 파일은 시트 범위가 수십만 행·열까지 잡혀
// 빈 칸(null)만으로 요청이 156MB 가 됐다(2026-10-07 綜合清关). 앞·중간 빈 행은 행 번호(line)가 걸려 있어 그대로 둔다(서버 outboundTrimAoa 와 같은 규칙)
export function trimAoa(aoa) {
  const filled = v => v !== null && v !== undefined && v !== '';
  let lastRow = -1;
  let width = 0;
  aoa.forEach((r, i) => {
    if (!Array.isArray(r)) return;
    for (let j = r.length - 1; j >= 0; j--) {
      if (filled(r[j])) { lastRow = i; if (j + 1 > width) width = j + 1; break; }
    }
  });
  return aoa.slice(0, lastRow + 1).map(r => (Array.isArray(r) && r.length > width ? r.slice(0, width) : r));
}

export default { isZip, u8ToBinaryString, stripZipEntries, stripXlsxMedia, trimAoa };
